import { useState } from 'react'

import { F0Dialog, F0Text } from '@factorialco/f0-react'
import {
  DeleteTicketingTicketCategoryDocument,
  TicketingTicketCategoriesDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { trackDeleteTicketCategory } from 'modules/ticketing/lib/tracking'

interface Props {
  category: { id: string; name: string }
  onClose: () => void
}

const DeleteCategoryDialog = ({ category, onClose }: Props) => {
  const [loading, setLoading] = useState(false)

  const [performDelete] = useMutation(DeleteTicketingTicketCategoryDocument, {
    extractorFn: (data) => data.ticketingMutations.deleteTicketingTicketCategory,
    refetchQueries: [TicketingTicketCategoriesDocument],
  })

  const handleConfirm = async () => {
    setLoading(true)
    const result = await performDelete({ variables: { id: category.id } })
    const handled = handleResult(result, () => {})
    setLoading(false)
    if (handled.success) {
      trackDeleteTicketCategory()
      app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })
      onClose()
    } else {
      const errors = handled.errors ?? []
      // The backend returns Outcome.validation_failed(base: [...]) when tickets
      // still reference this category. That surfaces as a StructuredError with
      // field === 'base'. Any other failure (network, permission) gets the generic message.
      const hasTickets = errors.some(
        (e) => '__typename' in e && e.__typename === 'StructuredError' && e.field === 'base'
      )
      app.setFlash({
        type: 'wrong',
        message: hasTickets
          ? i18n.t('ticketing.settings.categories.delete_blocked')
          : i18n.t('errors.generic_error'),
      })
    }
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      illustration="document-delete"
      title={i18n.t('ticketing.settings.categories.delete_confirm_title')}
      onClose={onClose}
      primaryAction={{
        label: i18n.t('common.destroy'),
        onClick: handleConfirm,
        loading,
        critical: true,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <F0Text
        content={i18n.t('ticketing.settings.categories.delete_confirm_message', {
          name: category.name,
        })}
      />
    </F0Dialog>
  )
}

export default DeleteCategoryDialog
